'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';

export default function RegisterPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [foodPreference, setFoodPreference] = useState('VEG');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
      const res = await fetch(`${authApi}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, firstName, lastName, foodPreference }),
      });

      if (res.ok) {
        setSuccess('Account created successfully! Redirecting to login...');
        setTimeout(() => {
          router.push('/login');
        }, 2000);
      } else {
        const errorMsg = await res.text();
        setError(errorMsg || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-deep-green p-4 sm:p-8">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <Image 
          src="https://images.unsplash.com/photo-1606859191214-25806e8e2423?q=80&w=2000&auto=format&fit=crop" 
          alt="Fresh Organic Produce" 
          fill 
          className="object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
      </div>

      {/* Centered Register Card */}
      <div className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur-md p-8 sm:p-10 rounded-3xl shadow-2xl border border-white/20 my-8">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex flex-col items-center justify-center group">
            <Image src="/images/pvlogo.png" alt="Pasumai Virumbi Logo" width={64} height={64} className="object-contain group-hover:scale-105 transition-transform duration-300 drop-shadow-md mb-2" />
            <h2 className="text-2xl font-display font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-deep-green to-mid-green">
              Pasumai Virumbi
            </h2>
            <div className="h-[2px] w-1/2 bg-gradient-to-r from-amber to-transparent rounded-full my-1" />
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-[0.2em]">100% Pure & Organic</p>
          </Link>
        </div>

        <div className="mb-6 text-center">
          <h3 className="text-xl font-bold text-deep-green mb-1">Create Account</h3>
          <p className="text-text-muted text-sm font-medium">Join us for fresh organic deliveries.</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3.5 rounded-lg mb-6 text-sm font-medium shadow-sm flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-3.5 rounded-lg mb-6 text-sm font-medium shadow-sm flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            {success}
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-deep-green mb-1.5">First Name</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none text-deep-green font-medium shadow-sm"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-deep-green mb-1.5">Last Name</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none text-deep-green font-medium shadow-sm"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-deep-green mb-1.5">Email Address</label>
            <input 
              type="email" 
              required
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none text-deep-green font-medium shadow-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-bold text-deep-green mb-1.5">Password</label>
            <input 
              type="password" 
              required
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none text-deep-green font-medium shadow-sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-deep-green mb-2">Food Preference</label>
            <div className="flex gap-6 mt-1.5">
              <label className="flex items-center gap-2 cursor-pointer font-medium text-deep-green">
                <input 
                  type="radio" 
                  name="foodPreference" 
                  value="VEG" 
                  checked={foodPreference === 'VEG'}
                  onChange={() => setFoodPreference('VEG')}
                  className="w-4 h-4 text-mid-green focus:ring-mid-green"
                />
                Pure Vegetarian 🌱
              </label>
              <label className="flex items-center gap-2 cursor-pointer font-medium text-deep-green">
                <input 
                  type="radio" 
                  name="foodPreference" 
                  value="NON_VEG" 
                  checked={foodPreference === 'NON_VEG'}
                  onChange={() => setFoodPreference('NON_VEG')}
                  className="w-4 h-4 text-mid-green focus:ring-mid-green"
                />
                Non-Vegetarian 🍗
              </label>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={isLoading || !!success}
            className={`w-full bg-mid-green text-white py-3.5 rounded-xl font-bold hover:bg-deep-green transition-all shadow-lg shadow-mid-green/20 mt-4 flex justify-center items-center ${(isLoading || !!success) ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-sm font-medium text-text-muted">
          Already have an account?{' '}
          <Link href="/login" className="text-amber font-bold hover:text-deep-green transition-colors hover:underline">
            Log in here
          </Link>
        </p>
      </div>
    </div>
  );
}
